/**
 * Number formatting for operator surfaces.
 *
 * Separate from `datetime.ts` and separate from `pricing.ts` on purpose. `Number.toLocaleString`
 * is banned by the same eslint rule that bans `Date.toLocaleString` — the rule cannot tell the
 * two apart, and the date case is the one that has actually caused a bug (a visit rendered in
 * the browser's zone rather than the operator's). One helper here is cheaper than an exception
 * to a rule that is right more often than it is wrong.
 */

const COUNT = new Intl.NumberFormat('en-US')

/** "12,480" — a count, grouped. */
export function count(value: number): string {
  return COUNT.format(value)
}

/** A fixed range of significant digits, so a column of costs lines up under `tabular-nums`. */
const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const USD_SMALL = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
})

/**
 * "$0.0431" or "$14.20" — an internal cost figure, for staff screens only.
 *
 * Four decimal places below a dollar because that is the scale AI costs actually live at: a
 * chat turn is about two tenths of a cent, and rounding it to "$0.00" turns the number that
 * decides whether this product is viable into a column of zeroes.
 *
 * **Never for homeowner-facing copy.** Material prices go through `pricing.ts`, which labels
 * them with the store and the date the way 01 §7 requires.
 */
export function money(value: number): string {
  return Math.abs(value) < 1 ? USD_SMALL.format(value) : USD.format(value)
}
